import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { fmtNum } from "../lib/format";
import SetupGuide from "./SetupGuide";
import { Boxes, Package, Droplets, Bed, Warehouse, Wrench, SearchX, TriangleAlert, ShieldCheck, CalendarClock } from "lucide-react";

const CARDS = [
  { key: "tagged_total", label: "Tagged Assets", icon: Boxes, to: "/assets/master" },
  { key: "bulk_total", label: "Bulk Reusable Qty", icon: Package, to: "/assets/store" },
  { key: "consumable_store", label: "Consumable Store Balance", icon: Droplets, to: "/assets/store" },
  { key: "tagged_in_rooms", label: "Assets in Rooms", icon: Bed, to: "/assets/rooms" },
  { key: "tagged_in_store", label: "Assets in Store", icon: Warehouse, to: "/assets/master" },
  { key: "under_repair", label: "Under Repair", icon: Wrench, to: "/assets/maintenance" },
  { key: "missing_open", label: "Missing (Investigation)", icon: SearchX, to: "/assets/incidents" },
  { key: "damaged_holding_qty", label: "Damaged Holding Qty", icon: TriangleAlert, to: "/assets/incidents" },
];

export default function AssetOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/assets/dashboard").then(({ data }) => setD(data)); }, []);

  return (
    <div data-testid="asset-overview">
      <SetupGuide />
      {d && !d.setup.opening_finalized && (
        <Card className="p-4 mb-6 border-amber-300 bg-amber-50" data-testid="setup-pending-banner">
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
            <TriangleAlert className="w-4 h-4" /> Setup Pending — opening stock is not finalized. Balances below exclude draft opening quantities.
            <Link to="/assets/settings" className="underline ml-1">Complete setup</Link>
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="asset-kpi-grid">
        {CARDS.map(({ key, label, icon: Icon, to }) => (
          <Link to={to} key={key}>
            <Card className="kpi-card hover:border-slate-400 transition-colors" data-testid={`asset-kpi-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="kpi-label">{label}</div>
                <Icon className="w-4 h-4 text-slate-400" />
              </div>
              <div className="kpi-value">{d ? fmtNum(d[key]) : "—"}</div>
            </Card>
          </Link>
        ))}
        <Card className="kpi-card" data-testid="asset-kpi-value">
          <div className="flex items-center justify-between mb-2">
            <div className="kpi-label">Total Purchase Value</div>
            <ShieldCheck className="w-4 h-4 text-slate-400" />
          </div>
          <div className="kpi-value">{d?.value_available ? `₹${fmtNum(d.total_value)}` : "—"}</div>
          {!d?.value_available && <div className="text-xs text-slate-400 mt-1">Value Not Available</div>}
        </Card>
        <Link to="/assets/warranty">
          <Card className="kpi-card hover:border-slate-400" data-testid="asset-kpi-amc">
            <div className="flex items-center justify-between mb-2">
              <div className="kpi-label">AMC Due / Overdue</div>
              <CalendarClock className="w-4 h-4 text-slate-400" />
            </div>
            <div className="kpi-value">{d ? `${d.amc_due_soon.length} / ${d.amc_overdue.length}` : "—"}</div>
          </Card>
        </Link>
        <Link to="/assets/warranty">
          <Card className="kpi-card hover:border-slate-400" data-testid="asset-kpi-warranty">
            <div className="flex items-center justify-between mb-2">
              <div className="kpi-label">Warranty Due / Expired</div>
              <CalendarClock className="w-4 h-4 text-slate-400" />
            </div>
            <div className="kpi-value">{d ? `${d.warranty_due_soon.length} / ${d.warranty_expired.length}` : "—"}</div>
          </Card>
        </Link>
      </div>

      {d?.low_stock?.length > 0 && (
        <Card className="p-4 mb-6 border-amber-300 bg-amber-50" data-testid="asset-low-stock">
          <div className="flex items-center gap-2 mb-2 text-amber-900 font-semibold">
            <TriangleAlert className="w-4 h-4" /> Low-stock Consumables
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            {d.low_stock.map(a => (
              <div key={a.item_id} className="flex items-center justify-between border-b border-amber-200 pb-1">
                <span className="font-medium text-slate-800">{a.name}</span>
                <span className="text-amber-900">{a.store} / min {a.min_level}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="section-sub mb-3 font-medium text-slate-700">Recent Purchases</div>
          {d?.recent_purchases?.length ? (
            <table className="vr-table"><thead><tr><th>Number</th><th>Vendor</th><th>Date</th><th>Lines</th></tr></thead>
              <tbody>{d.recent_purchases.map(p => (
                <tr key={p.id}><td>{p.number}</td><td>{p.vendor_name || "—"}</td><td>{p.date}</td><td>{p.lines?.length}</td></tr>
              ))}</tbody></table>
          ) : <div className="text-sm text-slate-500">No approved purchases yet.</div>}
        </Card>
        <Card className="p-4">
          <div className="section-sub mb-3 font-medium text-slate-700">Recent Transfers</div>
          {d?.recent_transfers?.length ? (
            <table className="vr-table"><thead><tr><th>Number</th><th>Asset/Qty</th><th>From → To</th><th>By</th></tr></thead>
              <tbody>{d.recent_transfers.map(t => (
                <tr key={t.id}><td>{t.number}</td><td>{t.asset_id || `${t.qty} pcs`}</td>
                  <td className="text-xs">{t.from_location?.replace("A_", "")} → {t.to_location?.replace("A_", "")}</td>
                  <td className="text-xs">{t.created_by_name}</td></tr>
              ))}</tbody></table>
          ) : <div className="text-sm text-slate-500">No transfers yet.</div>}
        </Card>
      </div>
    </div>
  );
}
